import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { EMPTY, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Message } from '../../models/core.models';
import { MessagesFirebaseService } from '../../services/firebase';
import {
  FilterMessages,
  LoadConversation,
  LoadMessages,
  MarkConversationAsRead,
  MarkMessageAsRead,
  SearchMessages,
  SendMessage,
  SetMessagesError,
  SetMessagesLoading,
  SetMessagesPage,
} from './messages.actions';

export interface MessagesStateModel {
  messages: Message[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conversations: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedConversation: any | null;
  filteredMessages: Message[];
  loading: boolean;
  error: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any;
  searchQuery: string;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}

@State<MessagesStateModel>({
  name: 'messages',
  defaults: {
    messages: [],
    conversations: [],
    selectedConversation: null,
    filteredMessages: [],
    loading: false,
    error: null,
    filters: {},
    searchQuery: '',
    pagination: {
      page: 1,
      pageSize: 20,
      total: 0,
    },
  },
})
@Injectable()
export class MessagesState {
  private messagesService = inject(MessagesFirebaseService);

  constructor() {
    /* noop */
  }

  @Action(LoadMessages)
  loadMessages(ctx: StateContext<MessagesStateModel>) {
    ctx.patchState({ loading: true, error: null });

    return this.messagesService.getAll().pipe(
      map((messages) => {
        ctx.patchState({
          messages,
          filteredMessages: messages,
          loading: false,
          pagination: {
            ...ctx.getState().pagination,
            total: messages.length,
          },
        });
        return messages;
      }),
      catchError((_error) => {
        ctx.patchState({
          loading: false,
          error: 'Failed to load messages',
        });
        return EMPTY;
      }),
    );
  }

  @Action(LoadConversation)
  loadConversation(ctx: StateContext<MessagesStateModel>, action: LoadConversation) {
    const state = ctx.getState();
    const conversation = state.conversations.find((c) => c.id === action.conversationId);

    if (conversation) {
      ctx.patchState({ selectedConversation: conversation });
    }
  }

  @Action(SendMessage)
  sendMessage(ctx: StateContext<MessagesStateModel>, action: SendMessage) {
    ctx.patchState({ loading: true, error: null });

    const newMessage: Omit<Message, 'id'> = {
      ...action.payload,
      type: 'text',
      isRead: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return from(this.messagesService.add(newMessage)).pipe(
      switchMap((messageId) => this.messagesService.getById(messageId)),
      map((message) => {
        if (message) {
          const state = ctx.getState();
          const messages = [...state.messages, message];
          ctx.patchState({
            messages,
            filteredMessages: messages,
            loading: false,
            pagination: {
              ...state.pagination,
              total: messages.length,
            },
          });
        }
        return message;
      }),
      catchError((_error) => {
        ctx.patchState({
          loading: false,
          error: 'Failed to send message',
        });
        return EMPTY;
      }),
    );
  }

  @Action(MarkMessageAsRead)
  markMessageAsRead(ctx: StateContext<MessagesStateModel>, action: MarkMessageAsRead) {
    const state = ctx.getState();

    return from(this.messagesService.markAsRead(action.messageId)).pipe(
      switchMap(() => this.messagesService.getById(action.messageId)),
      map((updatedMessage) => {
        if (updatedMessage) {
          const messages = state.messages.map((message) =>
            message.id === action.messageId ? updatedMessage : message,
          );

          ctx.patchState({
            messages,
            filteredMessages: messages,
          });
        }
        return updatedMessage;
      }),
      catchError((_error) => {
        return EMPTY;
      }),
    );
  }

  @Action(MarkConversationAsRead)
  markConversationAsRead(ctx: StateContext<MessagesStateModel>, action: MarkConversationAsRead) {
    const state = ctx.getState();
    const messages = state.messages.map((message) =>
      message.senderId === action.conversationId || message.receiverId === action.conversationId
        ? { ...message, isRead: true, readAt: new Date().toISOString() }
        : message,
    );

    ctx.patchState({
      messages,
      filteredMessages: messages,
    });
  }

  @Action(FilterMessages)
  filterMessages(ctx: StateContext<MessagesStateModel>, action: FilterMessages) {
    const state = ctx.getState();
    let filteredMessages = [...state.messages];

    // Apply filters
    if (action.filters.senderId) {
      filteredMessages = filteredMessages.filter((m) => m.senderId === action.filters.senderId);
    }
    if (action.filters.receiverId) {
      filteredMessages = filteredMessages.filter((m) => m.receiverId === action.filters.receiverId);
    }
    if (action.filters.bookingId) {
      filteredMessages = filteredMessages.filter((m) => m.bookingId === action.filters.bookingId);
    }
    if (action.filters.isRead !== undefined) {
      filteredMessages = filteredMessages.filter((m) => m.isRead === action.filters.isRead);
    }
    if (action.filters.type) {
      filteredMessages = filteredMessages.filter((m) => m.type === action.filters.type);
    }

    ctx.patchState({
      filteredMessages,
      filters: action.filters,
      pagination: {
        ...state.pagination,
        page: 1,
        total: filteredMessages.length,
      },
    });
  }

  @Action(SearchMessages)
  searchMessages(ctx: StateContext<MessagesStateModel>, action: SearchMessages) {
    const state = ctx.getState();
    const query = action.query.toLowerCase();

    const filteredMessages = state.messages.filter((message) =>
      message.content.toLowerCase().includes(query),
    );

    ctx.patchState({
      filteredMessages,
      searchQuery: action.query,
      pagination: {
        ...state.pagination,
        page: 1,
        total: filteredMessages.length,
      },
    });
  }

  @Action(SetMessagesPage)
  setMessagesPage(ctx: StateContext<MessagesStateModel>, action: SetMessagesPage) {
    const state = ctx.getState();
    ctx.patchState({
      pagination: {
        ...state.pagination,
        page: action.page,
      },
    });
  }

  @Action(SetMessagesLoading)
  setMessagesLoading(ctx: StateContext<MessagesStateModel>, action: SetMessagesLoading) {
    ctx.patchState({ loading: action.loading });
  }

  @Action(SetMessagesError)
  setMessagesError(ctx: StateContext<MessagesStateModel>, action: SetMessagesError) {
    ctx.patchState({ error: action.error });
  }
}
